import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Plus, Edit2, Trash2, X, Check, AlertCircle, Loader2, Search, Eye } from 'lucide-react';
import bagianService from '../services/bagian';
import DeleteConfirmModal from './DeleteConfirmModal';
import PaginationBar from './PaginationBar';
import usePagination from '../hooks/usePagination';

const MasterBagian = () => {
  const [bagianList, setBagianList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBagian, setEditingBagian] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewDetail, setViewDetail] = useState(null);

  // filteredBagian harus sebelum usePagination untuk hindari Temporal Dead Zone
  const filteredBagian = React.useMemo(() => {
    if (!bagianList.length) return [];
    return bagianList.filter(b =>
      !searchTerm || b.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [bagianList, searchTerm]);

  const { paginatedItems, currentPage, totalPages, totalItems, startIndex, endIndex, goToPage } = usePagination(filteredBagian, 10);

  const fetchBagian = useCallback(async () => {
    try {
      setLoading(true);
      const data = await bagianService.getAllBagian();
      setBagianList(data.bagian || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBagian(); }, [fetchBagian]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const resetForm = () => {
    setFormData({ name: '', description: '' });
    setFormError('');
    setEditingBagian(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!formData.name.trim()) {
      setFormError('Nama bagian wajib diisi');
      return;
    }
    setFormLoading(true);
    try {
      if (editingBagian) {
        await bagianService.updateBagian(editingBagian._id, formData);
        showSuccess('Bagian updated successfully');
      } else {
        await bagianService.createBagian(formData);
        showSuccess('Bagian created successfully');
      }
      await fetchBagian();
      setShowModal(false);
      resetForm();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm || isDeleting) return;
    setIsDeleting(true);
    try {
      await bagianService.deleteBagian(deleteConfirm._id);
      setDeleteConfirm(null);
      await fetchBagian();
      showSuccess('Bagian deleted successfully');
    } catch (err) {
      setError(err.message);
      setDeleteConfirm(null);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Master Bagian</h2>
          <p className="mt-1 text-sm text-gray-500">Manage departments and divisions</p>
        </div>
        <button onClick={() => { resetForm(); setShowModal(true); }} className="btn-primary text-sm">
          <Plus className="w-4 h-4" /><span>Add Bagian</span>
        </button>
      </div>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 animate-slide-up">
          <div className="flex items-center space-x-2"><Check className="w-5 h-5 text-green-500" /><p className="text-sm font-medium text-green-800">{successMessage}</p></div>
        </div>
      )}

      <div className="card">
        <div className="px-4 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input type="text" placeholder="Search bagian..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-8 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 hover:bg-white focus:bg-white" />
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Nama Bagian</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-50">
              {paginatedItems.map(b => (
                <tr key={b._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{b.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{b.description || '-'}</td>
                  <td className="px-4 py-3">
                    {b.isActive !== false ? (
                      <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700"><span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5" />Active</span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700"><span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1.5" />Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end space-x-1">
                      <button onClick={() => setViewDetail(b)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" title="Lihat detail"><Eye className="w-4 h-4" /></button>
                      <button onClick={() => { setEditingBagian(b); setFormData({ name: b.name, description: b.description || '' }); setShowModal(true); }}
                        className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => setDeleteConfirm(b)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredBagian.length === 0 && (
                <tr><td colSpan="4" className="px-4 py-12 text-center text-gray-500">No bagian found</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <PaginationBar currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} goToPage={goToPage} />
      </div>

      {/* View Detail Modal */}
      {viewDetail && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => setViewDetail(null)}>
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full z-10 animate-scale-in" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Detail Bagian</h3>
                    <p className="text-sm text-gray-500">Informasi lengkap bagian</p>
                  </div>
                </div>
                <button onClick={() => setViewDetail(null)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div className="flex items-center space-x-4 pb-4 border-b border-gray-100">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">{viewDetail.name}</h4>
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full mt-1 ${
                      viewDetail.isActive !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${viewDetail.isActive !== false ? 'bg-green-500' : 'bg-red-500'}`} />
                      {viewDetail.isActive !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Deskripsi</label>
                    <p className="text-sm font-semibold text-gray-900">{viewDetail.description || <span className="text-gray-400 italic">Tidak ada deskripsi</span>}</p>
                  </div>
                  {viewDetail.createdAt && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Dibuat pada</label>
                      <p className="text-sm font-semibold text-gray-900">{new Date(viewDetail.createdAt).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end pt-2">
                  <button onClick={() => setViewDetail(null)} className="btn-secondary text-sm">
                    <X className="w-4 h-4" /><span>Tutup</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDeleteConfirm}
        title="Hapus Bagian"
        isLoading={isDeleting}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Nama Bagian</span>
          <span className="text-sm font-semibold text-gray-900">{deleteConfirm?.name}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Deskripsi</span>
          <span className="text-xs font-semibold text-gray-900 text-right max-w-[200px]">{deleteConfirm?.description || '-'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Status</span>
          <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${deleteConfirm?.isActive !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${deleteConfirm?.isActive !== false ? 'bg-green-500' : 'bg-red-500'}`} />
            {deleteConfirm?.isActive !== false ? 'Active' : 'Inactive'}
          </span>
        </div>
      </DeleteConfirmModal>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full z-10 animate-scale-in" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-5 border-b">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white"><Building2 className="w-5 h-5" /></div>
                  <div><h3 className="text-lg font-bold text-gray-900">{editingBagian ? 'Edit Bagian' : 'Add Bagian'}</h3></div>
                </div>
                <button onClick={() => { setShowModal(false); resetForm(); }} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
                {formError && <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-2"><AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-red-700">{formError}</p></div>}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Nama Bagian <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 hover:bg-white focus:bg-white" placeholder="e.g. IT, Keuangan" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                  <textarea value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} rows={2}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50 hover:bg-white focus:bg-white" placeholder="Optional description" />
                </div>
                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="btn-secondary text-sm">Cancel</button>
                  <button type="submit" disabled={formLoading} className="btn-primary text-sm">
                    {formLoading ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Saving...</span></> : <><Check className="w-4 h-4" /><span>{editingBagian ? 'Update' : 'Create'}</span></>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterBagian;
